import { createContext, useContext, useEffect, useState } from 'react'
import { UIMatch, useMatches } from 'react-router-dom'
import { ChildrenProp } from '@/common/types_and_interfaces.ts'

type PageDetail = { path: string; title: string; description: string }

type PageHeaderContextProps = {
  setTitle: (title: string) => void
}

const PageHeaderContext = createContext<PageHeaderContextProps | undefined>(
  undefined
)

const initPageTitle = { title: '', description: '' } as const
export const PageHeaderProvider = ({ children }: ChildrenProp) => {
  const [pageDetails, setPageDetails] =
    useState<Omit<PageDetail, 'path'>>(initPageTitle)
  const matches = useMatches()

  const lastMatchID = matches[matches.length - 1]['id'] ?? 0
  useEffect(() => {
    let match: UIMatch | undefined
    for (let index = 0; index < matches.length; index++) {
      if (matches[index]['handle'] !== undefined) {
        match = matches[index]
        break
      }
    }

    if (match && match.handle) {
      const matchHandler = match.handle as Record<string, never>
      if ('page_details' in matchHandler) {
        const pageDetails = matchHandler.page_details as Omit<
          PageDetail,
          'path'
        >
        setPageDetails(pageDetails)
      }
    }
  }, [lastMatchID])

  const setTitle = (title: string) => {
    setPageDetails((prevState) => {
      return { ...prevState, title }
    })
  }

  return (
    <PageHeaderContext.Provider value={{ setTitle }}>
      {pageDetails.title ||
        (pageDetails.description && (
          <div className="flex flex-col justify-center gap-2 py-4 mb-12 sm:mb-0">
            <h1 className="text-xl font-bold leading-none text-gray-900">
              {pageDetails.title}
            </h1>
            <div className="flex items-center gap-2 text-sm font-medium text-gray-500">
              {pageDetails.description}
            </div>
          </div>
        ))}

      {children}
    </PageHeaderContext.Provider>
  )
}

// eslint-disable-next-line
export const usePageHeader = () => {
  const context = useContext(PageHeaderContext)
  if (context === undefined)
    throw new Error('usePageHeader must be used within AppAlertProvider')
  return context
}
